import { MutableRefObject, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document } from 'react-pdf';

import { PDFJS_DOCUMENT_OPTIONS } from '../../../config/PdfJsConfig';
import { useElementSize } from '../../../hooks/ElementSize';
import { useZoomHandler } from '../../../hooks/ZoomHandler';
import { FileDocumentControls } from './FileDocumentControls';
import { FileDocumentLoadingIndicator } from './FileDocumentLoadingIndicator';
import { PDFPage } from './PDFPage';

import { QBox } from '@qualio/ui-components';
import styles from './PDFDocumentViewer.module.css';

export const PDFDocumentViewer = ({
  attachmentPreviewUrl,
  showDownloadButton,
  handleDownload,
  handlePrint,
  commentsPermission,
  handleZoomChange,
  renderFileDocumentLoadingIndicator = true,
  isXlsx = false,
}: {
  attachmentPreviewUrl: string;
  showDownloadButton: boolean;
  handleDownload: () => void;
  handlePrint?: () => void;
  commentsPermission: number;
  renderFileDocumentLoadingIndicator?: boolean;
  handleZoomChange?: (zoom: number) => void;
  isXlsx: boolean;
}) => {
  const { t } = useTranslation();
  const [documentPageCount, setDocumentPageCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(1.0);
  const [isPaginated, setIsPaginated] = useState(false);
  const wrapper = useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
  const { width, height = 0 } = useElementSize(wrapper);
  const file = useMemo(
    () => ({
      url: attachmentPreviewUrl,
      withCredentials: true,
    }),
    [attachmentPreviewUrl],
  );

  const handleNavigate = (offset: number) => {
    setPageNumber((prevValue) => prevValue + offset);
  };

  const handleZoom = useZoomHandler(setZoom, handleZoomChange);

  const handlePagination = () => {
    setIsPaginated(!isPaginated);
  };

  const onLoadSuccess = (pdf: any) => {
    setDocumentPageCount(pdf.numPages);
  };

  return (
    <>
      <FileDocumentControls
        handleNavigate={handleNavigate}
        handleZoom={handleZoom}
        handleDownload={handleDownload}
        totalPages={documentPageCount}
        currentPage={pageNumber}
        handlePagination={handlePagination}
        isPaginated={isXlsx || isPaginated}
        zoom={zoom}
        showDownloadButton={showDownloadButton}
        handlePrint={handlePrint}
        showCommentButton={commentsPermission > 1}
        isXlsx={isXlsx}
      />
      <QBox
        id={'fileWrapper'}
        data-cy="pdfDocumentViewer"
        className={
          styles[
            commentsPermission > 0
              ? 'file-document__pdf-wrapper-comments'
              : 'file-document__pdf-wrapper'
          ]
        }
        ref={wrapper}
      >
        <Document
          className={styles['react-pdf-element']}
          file={file}
          options={PDFJS_DOCUMENT_OPTIONS}
          onLoadSuccess={onLoadSuccess}
          loading={
            renderFileDocumentLoadingIndicator ? (
              <FileDocumentLoadingIndicator />
            ) : (
              ''
            )
          }
          error={t('errors.failedToLoadPreview')}
        >
          <PDFPage
            pageNumber={pageNumber}
            pageCount={documentPageCount}
            isPaginated={isXlsx || isPaginated}
            width={width}
            height={height}
            zoom={zoom}
          />
        </Document>
      </QBox>
    </>
  );
};
